import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface SplashScreenProps {
  message?: string;
  /** Quando false, inicia o fade-out. Após a animação, onFadeOutComplete é chamado. */
  visible?: boolean;
  /** Chamado quando o fade-out termina. Use para remover o overlay do DOM. */
  onFadeOutComplete?: () => void;
}

const FADE_OUT_DURATION_MS = 2000;

const SplashScreen: React.FC<SplashScreenProps> = ({
  message = 'Carregando sistema...',
  visible = true,
  onFadeOutComplete,
}) => {
  const theme = useTheme();
  const [opacity, setOpacity] = useState(1);
  const [isExiting, setIsExiting] = useState(false);
  const [sensorCount, setSensorCount] = useState(0);
  const onCompleteRef = useRef(onFadeOutComplete);
  onCompleteRef.current = onFadeOutComplete;

  useEffect(() => {
    if (visible) {
      setOpacity(1);
      setIsExiting(false);
      return;
    }
    setIsExiting(true);
    const start = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOpacity(0);
      });
    });
    return () => cancelAnimationFrame(start);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    setSensorCount(0);
    const id = window.setInterval(() => {
      setSensorCount((prev) => (prev + 1) % 999);
    }, 820);
    return () => window.clearInterval(id);
  }, [visible]);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const previousHtmlOverflow = htmlEl.style.overflow;
    const previousBodyOverflow = bodyEl.style.overflow;

    // Evita piscar/aparecer barra de rolagem enquanto o splash está visível.
    htmlEl.style.overflow = 'hidden';
    bodyEl.style.overflow = 'hidden';

    return () => {
      htmlEl.style.overflow = previousHtmlOverflow;
      bodyEl.style.overflow = previousBodyOverflow;
    };
  }, []);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'opacity') return;
    if (isExiting) onCompleteRef.current?.();
  };

  return (
    <Box
      onTransitionEnd={handleTransitionEnd}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        opacity,
        transition: `opacity ${FADE_OUT_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        pointerEvents: opacity > 0 ? 'auto' : 'none',
        // Camada própria na GPU para evitar piscadas e travamentos
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        contain: 'paint',
        willChange: isExiting ? 'opacity' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : '#f5f5f5',
        gap: 3,
        px: 3,
      }}
    >
      {/* Marca animada: M aparece e depois completa para Mestti */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          lineHeight: 1,
          transform: 'translateZ(0)',
          '@keyframes brandMIn': {
            '0%': { opacity: 0, transform: 'translateY(8px) scale(0.92)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          '@keyframes brandRestIn': {
            '0%': { opacity: 0, transform: 'translateX(-6px)' },
            '100%': { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes letterIn': {
            '0%': { opacity: 0, transform: 'translateY(6px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: { xs: '5rem', sm: '6rem', md: '7rem' },
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: theme.palette.primary.main,
            fontFamily: 'inherit',
            animation: 'brandMIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both',
          }}
        >
          M
        </Typography>
        <Typography
          component="span"
          sx={{
            ml: 0.2,
            fontSize: { xs: '5rem', sm: '6rem', md: '7rem' },
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: theme.palette.primary.main,
            fontFamily: 'inherit',
            display: 'inline-flex',
            gap: '0.01em',
          }}
        >
          {['E', 'S', 'T', 'T', 'I'].map((letter, index) => (
            <Box
              key={`${letter}-${index}`}
              component="span"
              sx={{
                display: 'inline-block',
                opacity: 0,
                animation: `letterIn 480ms cubic-bezier(0.22, 1, 0.36, 1) ${520 + index * 140}ms both`,
              }}
            >
              {letter}
            </Box>
          ))}
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 500,
        }}
      >
        {message}
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: 340,
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: 44,
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(3,69,120,0.05)',
            '@keyframes sensorBoxMove': {
              '0%': { transform: 'translateX(-28px)' },
              '100%': { transform: 'translateX(368px)' },
            },
            '@keyframes sensorPulse': {
              '0%, 68%, 100%': { opacity: 0.2, transform: 'scaleY(0.95)' },
              '75%, 85%': { opacity: 1, transform: 'scaleY(1)' },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 8,
              height: 4,
              borderRadius: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(0,0,0,0.08)',
            }}
          />

          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: 10,
                left: -28,
                width: 20,
                height: 20,
                borderRadius: 0.8,
                border: '1px solid #8B5A2B',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? '#9A6A3A'
                    : '#C99352',
                animation: `sensorBoxMove 2.46s linear ${index * 0.82}s infinite`,
                willChange: 'transform',
              }}
            />
          ))}

          <Box
            sx={{
              position: 'absolute',
              left: '56%',
              top: 7,
              width: 3,
              height: 30,
              borderRadius: 2,
              backgroundColor: '#FF2D2D',
              boxShadow: '0 0 12px rgba(255, 45, 45, 0.8)',
              animation: 'sensorPulse 2.46s linear infinite',
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: 20,
            mt: 0.2,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 'calc(56% - 16px)',
              top: 2,
              width: 32,
              height: 14,
              borderRadius: 999,
              backgroundColor: '#F2C300',
              border: '2px solid #A37800',
              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 'calc(56% - 5px)',
              top: 5,
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: '#4A1C1C',
              border: '1px solid #2A0F0F',
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: theme.palette.text.secondary,
          }}
        >
          CONTADOR: {String(sensorCount).padStart(3, '0')}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 32,
          color: theme.palette.text.disabled,
          fontSize: '0.75rem',
        }}
      >
        MESTTI - Sistema de Gestão Industrial
      </Typography>
    </Box>
  );
};

export default SplashScreen;
